import { groupBy, mapValues, uniq } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { withoutZeroEntries } from '@paragon/client-tools';
import { DownloadRequest, TrussEnvelope } from '@paragon/contract-library';
import { getComponentDesignQuantities, getFlattenedChildrenWithCalculatedQuantity } from '@paragon/layout-models';
import { assertExhaustiveSwitch, notNullish } from '@paragon/utilities';

import { isConsideredDesignItem, SelectableItemEntry } from '../../library/selection/types';
import { RootState } from '../index';
import { getAnalysisSetGuidToComponentDesignGuid } from './analysisSetsSelectors';
import { getComponentDesignGuidToQuantity } from './componentDesignQuantitySelectors';
import { getActiveAnalysisSet } from './getActiveAnalysisSetSelector';
import { getActiveComponentDesignGuid } from './getActiveComponentDesignSelector';
import { getActiveStation } from './getActiveStation';
import { getCommittedComponentDesigns } from './getComponentDesignsSelectors';
import { getContextCollectionName } from './getContextCollectionName';
import { getCurrentGroupedProductionComponentInstance } from './getCurrentGroupedProductionComponentInstance';
import { getProjectLayoutModel } from './getProjectLayoutModel';
import { getSelectedItemEntries } from './getSelectedItemSelector';
import { isInsertingComponentDesign } from './isInsertingComponentDesignSelector';
import { anyLayoutItemsExist } from './layoutModeSelectors';
import { getQuantitiesForActiveScheduleLineGroup } from './scheduleStationSelectors';
import { getAnalysisSetGuidToQuantity } from './sharedComponentSets';
import { isViewingOldAnalysisSet } from './viewOnlyModeSelectors';

export const getDownloadRequestGuidTypeForContext = createSelector(
  (state: RootState) => state.application.openContext?.type,

  (openContext) => {
    switch (openContext) {
      case 'project':
      case 'station':
      case 'job':
      case undefined:
        return 'ComponentDesign';
      case 'schedule-station':
      case 'shared-component-set': {
        return 'AnalysisSet';
      }
      default:
        assertExhaustiveSwitch(openContext);
    }
  },
);

export function getInitiallySelectedComponentDesignGuidsFromContextForSelectedEntries(
  selectedEntries: readonly SelectableItemEntry[],
  analysisSetGuidToComponentDesignGuid: { [guid: string]: string },
  activeComponentDesignGuid: string | null,
  trussEnvelopes: { [guid: string]: TrussEnvelope },
) {
  let initiallySelectedComponentDesignGuids = selectedEntries
    .map((entry) =>
      getComponentDesignGuidFromEntry(entry, analysisSetGuidToComponentDesignGuid, activeComponentDesignGuid),
    )
    .filter(notNullish);
  for (const trussGroupEntry of selectedEntries.filter((entry) => entry.type === 'trussGroup')) {
    const { trussEnvelopeGuids } = trussGroupEntry.item;
    const trussEnvelopeComponentDesignGuids = trussEnvelopeGuids
      .map((guid: string) => trussEnvelopes[guid])
      .filter(notNullish)
      .map((trussEnvelope: TrussEnvelope) => trussEnvelope.componentDesignGuid)
      .filter(notNullish);
    initiallySelectedComponentDesignGuids.push(...trussEnvelopeComponentDesignGuids);
  }

  initiallySelectedComponentDesignGuids = uniq(initiallySelectedComponentDesignGuids);

  return initiallySelectedComponentDesignGuids.length === 0 ? undefined : initiallySelectedComponentDesignGuids;
}

function getComponentDesignGuidFromEntry(
  entry: SelectableItemEntry,
  analysisSetGuidToComponentDesignGuid: { [guid: string]: string },
  activeComponentDesignGuid: string | null,
): string | null {
  switch (entry.type) {
    case 'componentDesign':
      return entry.item.guid;
    case 'trussEnvelope':
      return entry.item.componentDesignGuid;
    case 'productionComponentInstance':
      return analysisSetGuidToComponentDesignGuid[entry.item.analysisSetGuid];
    default:
      // If a subitem (plate, member, bearing, etc.) of the active component design is selected, return the active
      // component design GUID. Ignore other types.
      return isConsideredDesignItem(entry.type) ? activeComponentDesignGuid : null;
  }
}

export const getInitiallySelectedComponentDesignGuidToQuantityFromContext = createSelector(
  getSelectedItemEntries,
  getAnalysisSetGuidToComponentDesignGuid,
  getActiveComponentDesignGuid,
  getComponentDesignGuidToQuantity,
  (state: RootState) => state.layout.trussEnvelopes,

  (
    selectedEntries,
    analysisSetGuidToComponentDesignGuid,
    activeComponentDesignGuid,
    componentDesignGuidToQuantity,
    trussEnvelopes,
  ) => {
    const initiallySelectedComponentDesignGuidsFromContextForSelectedEntries =
      getInitiallySelectedComponentDesignGuidsFromContextForSelectedEntries(
        selectedEntries,
        analysisSetGuidToComponentDesignGuid,
        activeComponentDesignGuid,
        trussEnvelopes,
      );
    const guidToQuantity: { [guid: string]: number } = {};
    for (const initiallySelectedComponentDesignGuid of initiallySelectedComponentDesignGuidsFromContextForSelectedEntries ??
      []) {
      const quantity = componentDesignGuidToQuantity[initiallySelectedComponentDesignGuid];
      if (quantity === undefined) {
        throw new Error('Quantity for component not found');
      }
      guidToQuantity[initiallySelectedComponentDesignGuid] = quantity;
    }

    // If truss envelopes or truss groups are selected, the component's quantity should be based on how many truss
    // envelopes that point to that component are selected
    const trussEnvelopeComponentDesignGuids = selectedEntries
      .flatMap((entry) =>
        entry.type === 'trussEnvelope'
          ? [entry.item.componentDesignGuid]
          : entry.type === 'trussGroup'
            ? entry.item.trussEnvelopeGuids.map((guid: string) => {
                const trussEnvelope = trussEnvelopes[guid];
                return trussEnvelope ? trussEnvelope.componentDesignGuid : null;
              })
            : [],
      )
      .filter(notNullish);
    const trussEnvelopeComponentDesignGuidQuantities = mapValues(
      groupBy(trussEnvelopeComponentDesignGuids, (componentDesignGuid) => componentDesignGuid),
      (componentDesignGuidGroup) => componentDesignGuidGroup.length,
    );
    for (const [trussEnvelopeComponentDesignGuid, quantity] of Object.entries(
      trussEnvelopeComponentDesignGuidQuantities,
    )) {
      guidToQuantity[trussEnvelopeComponentDesignGuid] = quantity;
    }

    return guidToQuantity;
  },
);

export const getDownloadRequestForBulk = createSelector(
  (state: RootState) => state.application.openContext?.type,
  getDownloadRequestGuidTypeForContext,
  getContextCollectionName,
  getProjectLayoutModel,
  getFlattenedChildrenWithCalculatedQuantity,
  (state: RootState) => state.sharedComponentSet.sharedComponentSet,
  getActiveStation,
  getQuantitiesForActiveScheduleLineGroup,
  getCommittedComponentDesigns,

  (
    openContext,
    downloadRequestGuidTypeForContext,
    contextCollectionName,
    projectLayoutModel,
    projectFlattenedChildrenWithCalculatedQuantity,
    sharedComponentSet,
    activeStation,
    quantitiesForActiveScheduleLineGroup,
    committedComponentDesigns,
  ): DownloadRequest | null => {
    const groupName = contextCollectionName ?? 'Components';
    const isInSharedComponentSetContext = openContext === 'shared-component-set' && sharedComponentSet;
    const sharedComponentSetLookupCode = isInSharedComponentSetContext ? sharedComponentSet.lookupCode : null;
    let guidToQuantity: { [guid: string]: number };
    switch (openContext) {
      case 'project':
        if (!projectLayoutModel) return null;
        guidToQuantity = getComponentDesignQuantities(
          projectFlattenedChildrenWithCalculatedQuantity(projectLayoutModel.guid),
        );
        break;

      case 'station':
        if (!activeStation || activeStation.followsSchedule) return null;
        guidToQuantity = mapValues(activeStation.componentDesigns, ({ quantity }) => quantity ?? 1);
        break;

      case 'schedule-station':
        if (!activeStation || !activeStation.followsSchedule) return null;
        guidToQuantity = quantitiesForActiveScheduleLineGroup;
        break;

      case 'shared-component-set': {
        if (!sharedComponentSet) return null;
        guidToQuantity = getAnalysisSetGuidToQuantity(sharedComponentSet);
        break;
      }

      case 'job':
      case undefined: {
        guidToQuantity = committedComponentDesigns.reduce<{
          [analysisSetGuid: string]: number;
        }>((guidToQuantity, componentDesign) => {
          guidToQuantity[componentDesign.guid] = 1;
          return guidToQuantity;
        }, {});
        break;
      }

      default:
        assertExhaustiveSwitch(openContext);
    }

    return {
      guidType: downloadRequestGuidTypeForContext,
      guidToQuantity: withoutZeroEntries(guidToQuantity),
      groupName,
      sharedComponentSetLookupCode,
    };
  },
);

export const getDownloadRequestForSingle = createSelector(
  (state: RootState) => state.application.openContext?.type,
  getDownloadRequestGuidTypeForContext,
  getContextCollectionName,
  (state: RootState) => state.sharedComponentSet.sharedComponentSet,
  getSelectedItemEntries,
  getActiveComponentDesignGuid,
  getAnalysisSetGuidToComponentDesignGuid,
  (state: RootState) => state.layout.trussEnvelopes,
  getComponentDesignGuidToQuantity,
  (state: RootState) => state.analysisSets.activeAnalysisSetGuid,
  getCurrentGroupedProductionComponentInstance,

  (
    openContext,
    downloadRequestGuidTypeForContext,
    contextCollectionName,
    sharedComponentSet,
    selectedItemEntries,
    activeComponentDesignGuid,
    analysisSetGuidToComponentDesignGuid,
    trussEnvelopes,
    componentDesignGuidToQuantity,
    activeAnalysisSetGuid,
    currentGroupedProductionComponentInstance,
  ): DownloadRequest | null => {
    let guidToQuantity: { [guid: string]: number };
    switch (openContext) {
      case 'project':
      case 'station':
      case 'job':
      case undefined:
        const initiallySelectedComponentDesignGuids =
          getInitiallySelectedComponentDesignGuidsFromContextForSelectedEntries(
            selectedItemEntries,
            analysisSetGuidToComponentDesignGuid,
            activeComponentDesignGuid,
            trussEnvelopes,
          );
        if (initiallySelectedComponentDesignGuids?.length !== 1) return null;
        guidToQuantity = {
          [initiallySelectedComponentDesignGuids[0]]:
            componentDesignGuidToQuantity[initiallySelectedComponentDesignGuids[0]],
        };
        break;

      case 'shared-component-set':
        if (!activeAnalysisSetGuid || !sharedComponentSet) return null;
        guidToQuantity = {
          [activeAnalysisSetGuid]: getAnalysisSetGuidToQuantity(sharedComponentSet)[activeAnalysisSetGuid] ?? 0,
        };
        break;

      case 'schedule-station':
        if (!currentGroupedProductionComponentInstance)
          throw new Error('No active production component instance found');
        guidToQuantity = {
          [currentGroupedProductionComponentInstance.analysisSetGuid]:
            currentGroupedProductionComponentInstance.quantity,
        };
        break;

      default:
        assertExhaustiveSwitch(openContext);
    }

    const groupName = contextCollectionName ?? 'Components';
    const isInSharedComponentSetContext = openContext === 'shared-component-set' && sharedComponentSet;
    const sharedComponentSetLookupCode = isInSharedComponentSetContext ? sharedComponentSet.lookupCode : null;
    return {
      guidType: downloadRequestGuidTypeForContext,
      guidToQuantity: withoutZeroEntries(guidToQuantity),
      groupName,
      sharedComponentSetLookupCode,
    };
  },
);

/**
 * Rewrites a download request to the `AnalysisSet` being viewed, for downloads which should reflect what is on screen
 * rather than the component's current state. Only applies when the request refers to exactly the component whose
 * analysis is being viewed, so that a download of some other component is never served this component's analysis.
 */
export const getRequestForAnalysisSetBeingViewed = createSelector(
  isViewingOldAnalysisSet,
  getActiveAnalysisSet,

  (viewingOldAnalysisSet, activeAnalysisSet) =>
    (downloadRequest: DownloadRequest): DownloadRequest => {
      if (!viewingOldAnalysisSet || !activeAnalysisSet || downloadRequest.guidType !== 'ComponentDesign')
        return downloadRequest;

      const requestedGuids = Object.keys(downloadRequest.guidToQuantity);
      if (requestedGuids.length !== 1 || requestedGuids[0] !== activeAnalysisSet.componentDesign.guid)
        return downloadRequest;

      return {
        ...downloadRequest,
        guidType: 'AnalysisSet',
        guidToQuantity: { [activeAnalysisSet.guid]: downloadRequest.guidToQuantity[requestedGuids[0]] },
      };
    },
);

export const getDownloadingEnabledSelector = createSelector(
  isInsertingComponentDesign,
  (state: RootState) => state.application.openContext?.type,
  getCommittedComponentDesigns,
  anyLayoutItemsExist,
  (isInsertingComponentDesign, openContextType, committedComponentDesigns, layoutPresent) => {
    if (isInsertingComponentDesign) return false;

    const componentsAreAvailableForDownload = committedComponentDesigns.length > 0;

    switch (openContextType) {
      // These contexts require component designs to be present to enable downloads
      case 'station':
      case 'schedule-station':
      case 'job':
      case undefined:
        return componentsAreAvailableForDownload;

      // Certain layout downloads are available even if there are no component designs
      case 'project':
      case 'shared-component-set':
        return componentsAreAvailableForDownload || layoutPresent;

      default:
        assertExhaustiveSwitch(openContextType);
    }
  },
);
